/**
 * React Hook for RTDB-cached NHL games with real-time updates
 *
 * Fixed: Race condition where fetch overwrites realtime cache data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { rtdbNHLService } from '../rtdbNHLService';
import nhlRTDBCache from '../rtdbCache';
import { CACHE_PATHS, NHL_API_CONFIG, getCurrentSeason, logCache } from '../rtdbConfig';

/**
 * Hook for fetching NHL games with RTDB caching and real-time updates
 * @param {Object} params - Query parameters
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {string|number} params.season - Season (4-digit integer 2025, or string '2025-26' or '2025')
 * @param {number} params.teamId - Team ID for filtering
 * @param {boolean} params.live - Fetch only live games
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Games data, loading state, error, and refetch function
 */
export const useRTDBGames = ({
  date,
  season,
  teamId,
  live = false,
  enabled = true,
  realtime = true
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  // Track if we have realtime data to prevent fetch from overwriting
  const hasRealtimeData = useRef(false);
  const abortControllerRef = useRef(null);

  // Convert season format consistently
  const normalizeSeasonForCache = useCallback((seasonParam) => {
    if (!seasonParam) return String(getCurrentSeason());
    return typeof seasonParam === 'string' ? seasonParam.split('-')[0] : String(seasonParam);
  }, []);

  const normalizeSeasonForAPI = useCallback((seasonParam) => {
    if (!seasonParam) return null;
    return typeof seasonParam === 'string' ? parseInt(seasonParam.split('-')[0]) : seasonParam;
  }, []);

  const fetchGames = useCallback(async (force = false) => {
    if (!enabled) return;

    // Don't fetch if we have realtime data unless forced
    if (hasRealtimeData.current && !force) {
      logCache('info', 'Skipping fetch - using realtime cached data');
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');

      logCache('info', 'useRTDBGames fetching', {
        date,
        season,
        teamId,
        live
      });

      let response;

      const apiSeason = normalizeSeasonForAPI(season);
      const leagueId = NHL_API_CONFIG.LEAGUE_ID;
      const timezone = NHL_API_CONFIG.DEFAULT_TIMEZONE;

      if (live) {
        response = await rtdbNHLService.getLiveGames();
      } else if (date) {
        response = await rtdbNHLService.getGamesByDate(date, leagueId, apiSeason, timezone);
      } else if (apiSeason) {
        response = await rtdbNHLService.getGamesForSeason(apiSeason, null, leagueId, timezone);
      } else {
        response = await rtdbNHLService.getTodayGames(leagueId, timezone);
      }

      // Only update if this request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        const games = response?.response || [];
        setData(games);
        setCacheStatus('success');
        logCache('info', `useRTDBGames loaded ${games.length} games`);
      }
    } catch (err) {
      // Don't set error if request was aborted
      if (err.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
        setError(err.message || 'Failed to load games');
        setCacheStatus('error');
        console.error('Failed to load NHL games:', err);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [date, season, teamId, live, enabled, normalizeSeasonForAPI]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enabled) return;

    let unsubscribe = () => {};
    let initialCheckDone = false;

    const apiSeason = normalizeSeasonForCache(season);

    // Build cache path - MUST match rtdbNHLService cache paths exactly
    let cachePath;

    if (live) {
      cachePath = `${CACHE_PATHS.GAMES_LIVE}/all`;
    } else if (date && teamId) {
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/team_${teamId}_${apiSeason}`;
    } else if (date) {
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/all_${apiSeason}`;
    }

    if (cachePath) {
      if (realtime) {
        // Subscribe to cache updates
        unsubscribe = nhlRTDBCache.subscribe(cachePath, (cachedGames) => {
          if (cachedGames) {
            setData(cachedGames);
            setCacheStatus('realtime-update');
            hasRealtimeData.current = true;
            setLoading(false);
            logCache('info', `useRTDBGames realtime update: ${cachedGames.length} games`);
          } else if (!initialCheckDone) {
            // First time, cache is empty - trigger fetch
            initialCheckDone = true;
            hasRealtimeData.current = false;
            fetchGames(true);
          }
        });
      } else {
        // No realtime - just do initial fetch
        hasRealtimeData.current = false;
        fetchGames(true);
      }
    } else {
      // No cache path - do regular fetch
      hasRealtimeData.current = false;
      fetchGames(true);
    }

    return () => {
      unsubscribe();
      hasRealtimeData.current = false;
      // Cancel any pending requests when unmounting or params change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [date, season, teamId, live, enabled, realtime, fetchGames, normalizeSeasonForCache]);

  return {
    games: data,
    loading,
    error,
    cacheStatus,
    refetch: () => fetchGames(true)
  };
};

export default useRTDBGames;
